import { ReactComponent as HolosafeFull } from '@assets/holosafe-logo-full.svg';
import { Flex, Grid, GridItem, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import CockpitPanelHeader from '@components/cockpit/panels/CockpitPanelHeader';
import Panel from '@components/cockpit/panels/Panel';
import {
  SystemParamFormFields,
  SystemStorage,
} from '@components/cockpit/panels/systemParameters/systemStorage/SystemStorage';
import { InputNumber, InputNumberProps } from '@components/ui/input/InputNumber';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasRole } from '@redux/authent/authent.selectors';
import { selectLicence } from '@redux/situation/monitoring.selectors';
import { RootState } from '@redux/store';
import { formatDate } from '@utils/date.utils';
import { Field, FieldInputProps, FormikProps } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';

import { RoleEnum } from '@/types/authent/roles.types';
import { DateTimeEnum } from '@/types/dateTime.types';
import { LicenceType } from '@/types/sensor/monitoring.types';

export const FormNumberInput = <FORM_FIELDS = SystemParamFormFields,>({
  unit,
  ...props
}: InputNumberProps & { unit: 'day' | 'hour' | 'minute' }) => {
  const { formatMessage } = useIntl();

  return (
    <Field name={props.name} type="number">
      {({ field, form }: { field: FieldInputProps<string>; form: FormikProps<FORM_FIELDS> }) => {
        const rawValue = form.values[props.name as keyof FORM_FIELDS];
        const numericValue = Number(rawValue);
        const isSingular = numericValue === 1;

        const translatedSuffix = formatMessage({
          id: isSingular ? `components.systemParameters.${unit}` : `components.systemParameters.${unit}s`,
        });

        return (
          <VStack gap={1}>
            <InputNumber
              allowMouseWheel
              {...field}
              {...props}
              onChange={(val) => form.setFieldValue(field.name, val)}
              invalid={
                !!form.touched[props.name as keyof FORM_FIELDS] && !!form.errors[props.name as keyof FORM_FIELDS]
              }
              suffix={translatedSuffix}
            />
          </VStack>
        );
      }}
    </Field>
  );
};

export function SystemParametersPanel() {
  const { formatMessage } = useIntl();
  const licence = useAppSelector(selectLicence);
  const canSeeStorage = useAppSelector((state: RootState) => hasRole(state, RoleEnum.ACCESS_STORAGE));

  return (
    <Panel header={<CockpitPanelHeader label={formatMessage({ id: 'components.sidebar.SYSTEM_PARAMETERS' })} />}>
      <Flex backgroundColor="neutral.700" height="100%" alignItems="center" justifyContent="center">
        <VStack gap={7} width="542px">
          <Icon as={HolosafeFull} width="320px" height="170px" />
          <HStack gap={1}>
            <Text color="neutral.300">
              <FormattedMessage id="components.systemParameters.version" />
            </Text>
            <Text fontWeight="medium">{__APP_VERSION__}</Text>
          </HStack>
          <Grid
            templateColumns="repeat(3,1fr)"
            templateRows="repeat(3,1fr)"
            columnGap={5}
            rowGap={1}
            paddingX={4}
            paddingY={3}
            borderWidth="2px"
            borderColor="neutral.900"
          >
            <GridItem width="219px">
              <Text color="neutral.300">{formatMessage({ id: 'components.systemParameters.key' })}</Text>
            </GridItem>
            <GridItem width="219px">
              <Text color="neutral.300">{formatMessage({ id: 'components.systemParameters.licence' })}</Text>
            </GridItem>
            <GridItem width="219px">
              <Text color="neutral.300">{formatMessage({ id: 'components.systemParameters.protection' })}</Text>
            </GridItem>
            <GridItem width="219px">
              <Text textStyle="xs">{licence?.key ?? '-'}</Text>
            </GridItem>
            <GridItem width="219px">
              <Text textStyle="xs">
                {licence ? (
                  <FormattedMessage
                    id={`components.systemParameters.licenceType.${licence.licence.type}`}
                    values={{
                      expirationDate: licence.licence.expirationDate
                        ? formatDate(licence.licence.expirationDate, DateTimeEnum.DATE_TIME)
                        : null,
                      counter:
                        licence.licence.type === LicenceType.EXECUTIONS ||
                        licence.licence.type === LicenceType.CONCURRENCY
                          ? licence.licence.counterMax - licence.licence.counter
                          : null,
                    }}
                  />
                ) : (
                  '-'
                )}
              </Text>
            </GridItem>
            <GridItem width="219px">
              <Text textStyle="xs">{licence?.protection ?? '-'}</Text>
            </GridItem>
          </Grid>
          <HStack gap={10} alignItems="top">
            {canSeeStorage && <SystemStorage />}
          </HStack>
        </VStack>
      </Flex>
    </Panel>
  );
}
